import { Filler, LineElement, PointElement, Chart as PolygonChart, RadialLinearScale, Tooltip } from "chart.js";
import React from "react";
import { Radar } from "react-chartjs-2";

import { Text } from "@performyard/py-components";
import { Competencies } from "@performyard/service-contracts";

import { getColor } from "../../../../utils/getColor";

PolygonChart.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip);

type CompetencyAxis = Competencies["company_wide"][number];

type CompetencyRadarProps = {
	title: string;
	axes: CompetencyAxis[];
};

const formatLevel = (value: number | null): string => (value === null ? "—" : `${value}`);

// 6-digit hex + "99" = 60% opacity, so overlapping polygons stay readable.
const withAlpha = (hex: string): string => `${hex}99`;

const deriveMax = (axes: CompetencyAxis[]): number | undefined => {
	const values = axes
		.flatMap((axis) => [axis.current, axis.expected])
		.filter((value): value is number => value !== null);
	if (values.length === 0) {
		return undefined;
	}
	return Math.ceil(Math.max(...values));
};

const RadarCard: React.FC<{ title: string; children: React.ReactNode; testId: string }> = ({
	title,
	children,
	testId,
}) => (
	<div
		className="flex flex-col gap-2 rounded-[10px] border border-solid border-neutral-2 bg-neutral-0 px-6 py-4"
		data-testid={testId}
	>
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9" overrideElement="h3">
			{title}
		</Text>
		{children}
	</div>
);

const LegendChip: React.FC<{ label: string; className: string }> = ({ label, className }) => (
	<div className={`flex items-center justify-center rounded p-1 ${className}`}>
		<Text size="extra-small" weight="semi-bold" color="neutral-9" wrap={false}>
			{label}
		</Text>
	</div>
);

export const CompetencyRadar: React.FC<CompetencyRadarProps> = ({ title, axes }) => {
	if (axes.length === 0) {
		return null;
	}

	if (axes.length < 3) {
		return (
			<RadarCard title={title} testId="competency-radar-fallback">
				<ul className="flex flex-col gap-1">
					{axes.map((axis) => (
						<li
							key={axis.label}
							className="flex justify-between gap-4"
							data-testid="competency-radar-fallback-row"
						>
							<Text size="paragraph-3" color="neutral-9">
								{axis.label}
							</Text>
							<Text size="paragraph-3" color="neutral-5">
								{formatLevel(axis.current)} / {formatLevel(axis.expected)}
							</Text>
						</li>
					))}
				</ul>
			</RadarCard>
		);
	}

	const suggestedMax = deriveMax(axes);

	return (
		<RadarCard title={title} testId="competency-radar">
			<div className="flex gap-2">
				<LegendChip label="Current" className="bg-green-2" />
				<LegendChip label="Expected" className="bg-primary-1" />
			</div>
			<div className="flex w-full items-center justify-center py-1">
				<div className="h-[200px] w-full">
					<Radar
						data={{
							labels: axes.map((axis) => axis.label),
							datasets: [
								{
									label: "Current",
									data: axes.map((axis) => axis.current),
									borderColor: getColor("green", 5),
									borderDash: [4, 4],
									backgroundColor: withAlpha(getColor("green", 2)),
									pointBackgroundColor: getColor("green", 5),
									pointBorderColor: getColor("green", 5),
									borderWidth: 1.5,
									pointRadius: 3,
									pointHoverRadius: 4,
								},
								{
									label: "Expected",
									data: axes.map((axis) => axis.expected),
									borderColor: getColor("primary", 5),
									backgroundColor: withAlpha(getColor("primary", 1)),
									pointBackgroundColor: getColor("primary", 5),
									pointBorderColor: getColor("primary", 5),
									borderWidth: 1.5,
									pointRadius: 3,
									pointHoverRadius: 4,
								},
							],
						}}
						options={{
							responsive: true,
							maintainAspectRatio: false,
							spanGaps: true,
							animation: {
								duration: 0.2,
							},
							plugins: {
								legend: {
									display: false,
								},
								tooltip: {
									displayColors: false,
									backgroundColor: getColor("neutral", 7),
									bodyFont: {
										size: 12,
										family: "Source Sans Pro, sans-serif",
									},
									caretPadding: 8,
									cornerRadius: 4,
									callbacks: {
										title: () => "",
										label: (context) => {
											if (context.raw === null || context.raw === undefined) {
												return [];
											}
											return [
												context.dataset.label ?? "",
												`${context.label}: ${context.formattedValue}`,
											];
										},
									},
								},
							},
							scales: {
								r: {
									suggestedMin: 0,
									suggestedMax,
									angleLines: {
										display: false,
									},
									grid: {
										color: getColor("neutral", 2),
										lineWidth: 1,
									},
									pointLabels: {
										color: getColor("neutral", 9),
										font: {
											size: 12,
											family: "Source Sans Pro",
											weight: 400,
											lineHeight: "16px",
										},
									},
									ticks: {
										stepSize: 1,
										display: false,
									},
								},
							},
						}}
					/>
				</div>
			</div>
		</RadarCard>
	);
};
